import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchCampaigns, createCampaign, updateCampaign, deleteCampaign, duplicateCampaign, Campaign } from '@/services/campaignService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function useCampaigns(companyId: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', companyId],
    queryFn: () => fetchCampaigns(companyId!),
    enabled: !!companyId,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createCampaign,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign created');
    },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Campaign> }) => updateCampaign(id, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign updated');
    },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteCampaign,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign deleted');
    },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useDuplicateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: duplicateCampaign,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign duplicated');
    },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useSendCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, preview, senderSource, scheduledFor }: { campaignId: string; preview: boolean; senderSource?: string; scheduledFor?: string }) => {
      const { data, error } = await supabase.functions.invoke('send-campaign', {
        body: { campaign_id: campaignId, preview, sender_source: senderSource, scheduled_for: scheduledFor },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      if (!variables.preview) {
        qc.invalidateQueries({ queryKey: ['campaigns'] });
        const msg = data?.status === 'scheduled'
          ? `Campaign scheduled! ${data?.queued_count || 0} emails queued.`
          : `Campaign sending started! ${data?.queued_count || 0} emails queued.`;
        toast.success(msg);
      }
    },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useStartSending() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (campaignId: string) => {
      const { error: updateErr } = await supabase
        .from('email_campaigns')
        .update({ status: 'sending', started_at: new Date().toISOString() })
        .eq('id', campaignId);
      if (updateErr) throw updateErr;

      const { data, error } = await supabase.functions.invoke('process-email-queue', {
        body: { campaign_id: campaignId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success(`Sending started! ${data?.processed || 0} emails processed.`);
    },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useProcessScheduledNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (campaignId: string) => {
      // Update campaign status to 'sending' and reset scheduled_for on queue items
      const { error: updateErr } = await supabase
        .from('email_campaigns')
        .update({ status: 'sending', started_at: new Date().toISOString(), scheduled_for: null })
        .eq('id', campaignId);
      if (updateErr) throw updateErr;

      // Set all pending queue items to be processable now
      const { error: queueErr } = await supabase
        .from('email_queue')
        .update({ scheduled_for: new Date().toISOString() })
        .eq('campaign_id', campaignId)
        .eq('status', 'pending');
      if (queueErr) throw queueErr;

      // Trigger process-email-queue
      const { data, error } = await supabase.functions.invoke('process-email-queue', {
        body: { campaign_id: campaignId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success(`Processing started! ${data?.processed || 0} emails processed.`);
    },
    onError: (e: any) => toast.error(e.message),
  });
}
